/**
 * InductionsList
 *
 * Displays induction batches with status management, harvest recording,
 * and per-batch performance metrics.
 */

import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '@/components/ui/dropdown-menu'
import { Plus, MoreHorizontal, Scissors, Loader2, Layers, ChevronDown, ChevronRight, Pencil } from 'lucide-react'
import NewInductionModal from './NewInductionModal'
import EditInductionModal from './EditInductionModal'
import RecordHarvestModal from './RecordHarvestModal'
import { INDUCTION_STATUSES } from '@/hooks/useSubstrateLogs'

const STATUS_COLOURS = {
  inducted: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  fruiting: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  harvesting: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300',
  resting: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
  spent: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
}

const STATUS_TRANSITIONS = {
  inducted: ['fruiting'],
  fruiting: ['harvesting'],
  harvesting: ['resting', 'spent'],
  resting: ['fruiting', 'spent'],
  spent: []
}

const STATUS_REVERSIONS = {
  inducted: null,
  fruiting: 'inducted',
  harvesting: 'fruiting',
  resting: 'harvesting',
  spent: 'resting'
}

function daysSince(dateStr) {
  const diff = Date.now() - new Date(dateStr + 'T00:00:00').getTime()
  return Math.floor(diff / (1000 * 60 * 60 * 24))
}

function formatDate(dateStr) {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric'
  })
}

export default function InductionsList({ hook }) {
  const {
    inductions, purchases, addInduction, updateInduction, updateInductionStatus,
    addHarvest, isLoading
  } = hook
  const [showNewModal, setShowNewModal] = useState(false)
  const [editInduction, setEditInduction] = useState(null)
  const [harvestInduction, setHarvestInduction] = useState(null)
  const [expandedId, setExpandedId] = useState(null)
  const [updating, setUpdating] = useState(null)

  const handleStatusChange = async (id, newStatus) => {
    setUpdating(id)
    try {
      await updateInductionStatus(id, newStatus)
    } catch (err) {
      console.error('Failed to update status:', err)
    } finally {
      setUpdating(null)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5" />
            Inductions
          </CardTitle>
          <Button onClick={() => setShowNewModal(true)} size="sm">
            <Plus className="mr-2 h-4 w-4" />
            New Induction
          </Button>
        </CardHeader>
        <CardContent>
          {inductions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No inductions recorded yet. Click "New Induction" to pull logs from cold storage.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Logs</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Harvested</TableHead>
                  <TableHead className="text-right">BE%</TableHead>
                  <TableHead className="text-right">Days</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {inductions.map((ind) => {
                  const isExpanded = expandedId === ind.id
                  const harvests = ind.substrate_induction_harvests || []
                  const bePercent = ind.total_substrate_weight_kg > 0
                    ? ((ind.total_harvest_kg / ind.total_substrate_weight_kg) * 100).toFixed(1)
                    : '0.0'
                  const nextStatuses = STATUS_TRANSITIONS[ind.status] || []
                  const revertTo = STATUS_REVERSIONS[ind.status]
                  const hasMenuItems = nextStatuses.length > 0 || revertTo

                  return (
                    <>
                      <TableRow key={ind.id} className="cursor-pointer" onClick={() => setExpandedId(isExpanded ? null : ind.id)}>
                        <TableCell>
                          {harvests.length > 0 && (
                            isExpanded
                              ? <ChevronDown className="h-4 w-4 text-muted-foreground" />
                              : <ChevronRight className="h-4 w-4 text-muted-foreground" />
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-sm">{ind.induction_code}</TableCell>
                        <TableCell>{formatDate(ind.induction_date)}</TableCell>
                        <TableCell className="text-right">{ind.log_count}</TableCell>
                        <TableCell>
                          <Badge className={STATUS_COLOURS[ind.status] || ''}>
                            {ind.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-medium">{ind.total_harvest_kg} kg</TableCell>
                        <TableCell className="text-right">{bePercent}%</TableCell>
                        <TableCell className="text-right text-muted-foreground">
                          {daysSince(ind.induction_date)}d
                        </TableCell>
                        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-1">
                            {ind.status !== 'spent' && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setHarvestInduction(ind)}
                              >
                                <Scissors className="mr-1 h-3 w-3" />
                                Harvest
                              </Button>
                            )}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" disabled={updating === ind.id}>
                                  {updating === ind.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <MoreHorizontal className="h-4 w-4" />
                                  )}
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="bg-white dark:bg-gray-900">
                                <DropdownMenuItem onClick={() => setEditInduction(ind)}>
                                  <Pencil className="mr-2 h-3.5 w-3.5" />
                                  Edit induction
                                </DropdownMenuItem>
                                {nextStatuses.length > 0 && (
                                  <>
                                    <DropdownMenuSeparator />
                                    {nextStatuses.map(status => (
                                      <DropdownMenuItem
                                        key={status}
                                        onClick={() => handleStatusChange(ind.id, status)}
                                      >
                                        Move to {status}
                                      </DropdownMenuItem>
                                    ))}
                                  </>
                                )}
                                {revertTo && <DropdownMenuSeparator />}
                                {revertTo && (
                                  <DropdownMenuItem
                                    onClick={() => handleStatusChange(ind.id, revertTo)}
                                    className="text-muted-foreground"
                                  >
                                    Revert to {revertTo}
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </TableCell>
                      </TableRow>

                      {/* Expanded harvest details */}
                      {isExpanded && harvests.length > 0 && (
                        <TableRow key={`${ind.id}-detail`}>
                          <TableCell colSpan={9} className="bg-muted/30 p-0">
                            <div className="px-8 py-3">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="text-muted-foreground">
                                    <th className="text-left py-1 font-medium">Date</th>
                                    <th className="text-right py-1 font-medium">Weight</th>
                                    <th className="text-center py-1 font-medium">Flush</th>
                                    <th className="text-center py-1 font-medium">Grade</th>
                                    <th className="text-left py-1 font-medium">By</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {harvests
                                    .sort((a, b) => new Date(b.harvest_date) - new Date(a.harvest_date))
                                    .map(h => (
                                      <tr key={h.id}>
                                        <td className="py-1">{formatDate(h.harvest_date)}</td>
                                        <td className="text-right py-1">{h.weight_kg} kg</td>
                                        <td className="text-center py-1">{h.flush_number}</td>
                                        <td className="text-center py-1">{h.quality_grade}</td>
                                        <td className="py-1 text-muted-foreground">{h.harvested_by || '-'}</td>
                                      </tr>
                                    ))
                                  }
                                </tbody>
                              </table>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  )
                })}
              </TableBody>
              <tfoot>
                <TableRow className="font-medium border-t">
                  <TableCell colSpan={3}>Total</TableCell>
                  <TableCell className="text-right">{inductions.reduce((sum, i) => sum + i.log_count, 0)}</TableCell>
                  <TableCell></TableCell>
                  <TableCell className="text-right">{inductions.reduce((sum, i) => sum + Number(i.total_harvest_kg || 0), 0)} kg</TableCell>
                  <TableCell colSpan={3}></TableCell>
                </TableRow>
              </tfoot>
            </Table>
          )}
        </CardContent>
      </Card>

      <NewInductionModal
        open={showNewModal}
        onOpenChange={setShowNewModal}
        onSubmit={addInduction}
        purchases={purchases}
      />

      <EditInductionModal
        open={!!editInduction}
        onOpenChange={(open) => !open && setEditInduction(null)}
        onSubmit={updateInduction}
        induction={editInduction}
        purchases={purchases}
      />

      <RecordHarvestModal
        open={!!harvestInduction}
        onOpenChange={(open) => !open && setHarvestInduction(null)}
        onSubmit={addHarvest}
        induction={harvestInduction}
      />
    </div>
  )
}
